import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useSidebarNavigation } from "../SidebarNavigation/useSidebarNavigation";

export const useOrderDetails = ({ orderId }) => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const { orderId: OrderIdFromParams } = useParams();
  const { hasService } = useSidebarNavigation();
  const isMounted = useRef(true);

  const [orderData, setOrderData] = useState(null);

  // Loading states for specific actions
  const [raisingRecon, setRaisingRecon] = useState(false);
  const [sendingSettlement, setSendingSettlement] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState({});
  const [reconLoading, setReconLoading] = useState({});

  const [isRaiseModalOpen, setIsRaiseModalOpen] = useState(false);
  const [categoryMapping, setCategoryMapping] = useState([]);
  const [referenceType, setReferenceType] = useState([]);
  const [issueDomains, setIssueDomains] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [raiseIssueFormData, setRaiseIssueFormData] = useState({
    domain: "",
    issue_category: "",
    issue_subcategory: "",
    short_desc: "",
    detailed_desc: "",
    // refs: [{ ref_type: "ORDER", ref_id: "" }], // Pre-set ORDER type
    refs: [
      {
        ref_type: "PAYMENT",
        ref_id: "",
      },
    ],
    images: [],
    city: "",
    bap_id: "",
    bap_uri: "",
  });
  const [selectedResolutions, setSelectedResolutions] = useState({});
  const [isResolutionSubmitting, setIsResolutionSubmitting] = useState(false);

  // Inside useOrderDetails.js

  // Fetch CategoryMapping and IssueDomains from GET_ISSUES API
  const fetchCategoryMapping = useCallback(async () => {
    try {
      const result = await apiHandler({
        url: `${endpoint.GET_ISSUES}?issue_type=REPORTED`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      const categoryMappingData = result?.data?.CategoryMapping || [];
      const issueDomainsData = result?.data?.filters?.issue_domain || [];
      const referenceTypesData = result?.data?.filters?.refs|| []; 
      if (isMounted.current) {
        setCategoryMapping(categoryMappingData);
        setIssueDomains(issueDomainsData);
        setReferenceType(referenceTypesData);
        // Debug log
      }
      // Return the data for use in openRaiseModal
      return { categoryMappingData, issueDomainsData, referenceTypesData };
    } catch (err) {
      console.error("Failed to fetch category mapping:", err);
      if (isMounted.current) {
        setCategoryMapping([]);
        setIssueDomains([]);
        setReferenceType([]);
      }
      return { categoryMappingData: [], issueDomainsData: [], referenceTypesData: [] };
    }
  }, [authData, dispatch, storeId]);
  // Inside useOrderDetails hook
  const openRaiseModal = useCallback(async () => {
    // Check if user has IGM service
    if (!hasService(6)) {
      addToast("KINDLY RAISE ISSUE THROUGH IGM!", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    if (!orderData) return;

    // Fetch category mapping and issue domains from GET_ISSUES API
    const { issueDomainsData, referenceTypesData } = await fetchCategoryMapping();

    // Get the current domain name from order
    const currentDomainName = orderData.domain || "";
    // Find the domain code from the fetched issue domains array
    const matchedDomain = issueDomainsData.find(
      (d) => d.name === currentDomainName
    );
    const currentDomainCode = matchedDomain?.code || "";

    setRaiseIssueFormData({
      // Pre-fill with the current order's domain code (read-only)
      domain: currentDomainCode,
      issue_category: "",
      issue_subcategory: "",
      short_desc: "",
      detailed_desc: "",
      // refs: [{ ref_type: "ORDER", ref_id: orderData.id }],
      refs: [
        {
          ref_type: "ORDER",
          ref_id: orderData?.id || "",
        },
      ],
      images: [],
      city: "",
      bap_id: orderData.buyer_app_details?.bap_id || "",
      bap_uri: orderData.buyer_app_details?.bap_uri || "",
    });

    setIsRaiseModalOpen(true);
  }, [orderData, fetchCategoryMapping, hasService, addToast]);
  const handleRefChange = (index, field, value) => {
    const newRefs = [...raiseIssueFormData.refs];
    newRefs[index] = { ...newRefs[index], [field]: value };
    setRaiseIssueFormData((prev) => ({ ...prev, refs: newRefs }));
  };

  const addRefRow = () => {
    setRaiseIssueFormData((prev) => ({
      ...prev,
      refs: [...prev.refs, { ref_type: "", ref_id: "" }],
    }));
  };

  const removeRefRow = (index) => {
    if (raiseIssueFormData.refs.length <= 1) return;
    const newRefs = raiseIssueFormData.refs.filter((_, i) => i !== index);
    setRaiseIssueFormData((prev) => ({ ...prev, refs: newRefs }));
  };

  const closeRaiseModal = () => setIsRaiseModalOpen(false);

  const handleCategoryChange = useCallback(
    (e) => {
      const categoryName = e.target.value;
      
      const categoryObj = categoryMapping.find(
        (c) => c.category === categoryName,
      );

      setRaiseIssueFormData((prev) => ({
        ...prev,
        issue_category: categoryName,
        issue_subcategory: "",
      }));
      setSubCategoryOptions(categoryObj ? categoryObj.sub_categories : []);
    },
    [categoryMapping],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const paramId = orderId || OrderIdFromParams;

  const fetchOrderDetails = useCallback(async () => {
    if (!paramId) return;
    setLoading(true);
    setError(null);

    try {
      const result = await apiHandler({
        url: `${endpoint.GET_ORDER_DETAIL}${paramId}`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      const data = result?.data?.data || result?.data;
      if (isMounted.current && data) {
        setOrderData(data);
      }
    } catch (err) {
      if (isMounted.current) setError("Failed to fetch order details.");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [paramId, authData, dispatch, storeId]);

  useEffect(() => {
    isMounted.current = true;
    fetchOrderDetails();
    return () => {
      isMounted.current = false;
    };
  }, [fetchOrderDetails]);

  // Helper to extract App Details
  const getAppDetails = () => {
    if (!orderData) return null;
    return {
      _id:
        orderData.settlementDetails?.mongoOrderId ||
        orderData._id ||
        orderData.id,
      buyer_app_id:
        orderData.buyer_app_details?.bap_id || orderData.context?.bap_id,
      buyer_app_uri:
        orderData.buyer_app_details?.bap_uri || orderData.context?.bap_uri,
      seller_app_id:
        orderData.sellerAppDetails?.url ||
        orderData.context?.bpp_id ||
        authData?.subscriberId,
      seller_app_uri:
        orderData.sellerAppDetails?.id ||
        orderData.context?.bpp_uri ||
        authData?.subscriberUri,
    };
  };

  const handleRaiseRecon = useCallback(
    async (settlementIdOrEvent) => {
      // 1. Prevent event crash
      if (
        settlementIdOrEvent &&
        typeof settlementIdOrEvent.preventDefault === "function"
      ) {
        settlementIdOrEvent.preventDefault();
        settlementIdOrEvent.stopPropagation();
      }

      // 2. Extract the Settlement ID ONLY if it is a string
      const targetSettlementId =
        typeof settlementIdOrEvent === "string" ? settlementIdOrEvent : null;

      if (!orderData) return;

      // Use mongoOrderId as the primary order_id for the payload
      const mongoOrderId =
        orderData.settlementDetails?.mongoOrderId || orderData._id;

      try {
        setRaisingRecon(true);

        const payload = {
          order_details: [
            {
              order_id: mongoOrderId,
              // Logic: Spread an object with the key only if targetSettlementId exists
              ...(targetSettlementId && { settlement_id: targetSettlementId }),
            },
          ],
          app_details: {
            buyer_app_id: orderData.buyer_app_details?.bap_id,
            buyer_app_uri: orderData.buyer_app_details?.bap_uri,
            seller_app_id: orderData.sellerAppDetails?.url,
            seller_app_uri: orderData.sellerAppDetails?.id,
          },
        };

        console.log("📤 Sending Recon Payload:", payload);

        const res = await apiHandler({
          url: `${endpoint.RAISE_RECON}`,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            tenantid: storeId,
          },
          authToken: authData,
          dispatch,
        });

        if (res?.status === 200 || res?.data?.result?.success) {
          addToast(
            res.data.message,
            { appearance: "success" },
          );
          fetchOrderDetails(); // Refresh to show new recon
        } else {
          addToast(
            res?.data?.response?.data?.failedOrders?.[0]?.message || "Failed to raise reconciliation",
            { appearance: "error" },
          );
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.result?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to process recon";
        addToast(errorMessage, { appearance: "error" });
      } finally {
        setRaisingRecon(false);
      }
    },
    [authData, dispatch, orderData, addToast, storeId, fetchOrderDetails],
  );

  // ------------------------------------
  // 2. HANDLE SEND SETTLEMENT REQUEST
  // ------------------------------------
  const handleLatestSettlement = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const details = getAppDetails();
      if (
        !details ||
        !details._id ||
        !details.buyer_app_id ||
        !details.seller_app_id
      ) {
        addToast("Missing order details for settlement.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      try {
        setSendingSettlement(true);
        const url = `${endpoint.SEND_SETTLEMENT_REQUEST}`; // Ensure this endpoint exists in your config
        const payload = {
          order_ids: [details._id],
          app_details: {
            buyer_app_id: details.buyer_app_id,
            buyer_app_uri: details.buyer_app_uri,
            seller_app_id: details.seller_app_id,
            seller_app_uri: details.seller_app_uri,
          },
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: { "Content-Type": "application/json" },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseData = response?.data;
        const message =
          responseData?.response?.data?.message ||
          "Settlement requested successfully";

        // Check for failed orders similar to recon
        const failedOrders = responseData?.response?.data?.failedOrders || [];

        if (failedOrders.length > 0) {
          addToast(`Failed: ${failedOrders[0].error}`, {
            appearance: "error",
            autoDismiss: true,
          });
        } else {
          addToast(message, { appearance: "success", autoDismiss: true });
          fetchOrderDetails(); // Refresh data to show new settlements
        }
      } catch (error) {
        //console.error("Settlement API Error:", error);
        addToast(
          error.response?.data?.message || "Failed to send settlement request.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      } finally {
        setSendingSettlement(false);
      }
    },
    [orderData, authData, dispatch, storeId, addToast, fetchOrderDetails],
  );

  const handleRaiseIssueSubmit = useCallback(async () => {
    // 1. Validation
    if (
      !raiseIssueFormData.issue_category ||
      !raiseIssueFormData.issue_subcategory ||
      !raiseIssueFormData.short_desc ||
      !raiseIssueFormData.city
    ) {
      addToast("Please fill all required fields", {
        appearance: "warning",
      });
      return;
    }

    try {
      setLoading(true); // Reuse existing loading state or create raisingIssue state

      // assembly multi-part form data for images
      const formData = new FormData();

      // Basic fields
      formData.append("domain", raiseIssueFormData.domain);
      formData.append("issue_category", raiseIssueFormData.issue_category);
      formData.append(
        "issue_subcategory",
        raiseIssueFormData.issue_subcategory,
      );
      formData.append("short_desc", raiseIssueFormData.short_desc);
      formData.append("detailed_desc", raiseIssueFormData.detailed_desc);
      formData.append("city", raiseIssueFormData.city);
      formData.append("bap_id", raiseIssueFormData.bap_id);
      formData.append("bap_uri", raiseIssueFormData.bap_uri);

      // References needs to be stringified
      formData.append("refs", JSON.stringify(raiseIssueFormData.refs));

      // Images
      if (raiseIssueFormData.images && raiseIssueFormData.images.length > 0) {
        raiseIssueFormData.images.forEach((file) => {
          formData.append("images", file);
        });
      }

      const response = await apiHandler({
        url: endpoint.RAISE_ISSUE,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        authToken: authData,
        dispatch,
        storeId,
      });

      if (response?.data?.result?.success || response?.status === 200) {
        addToast("Issue raised successfully", { appearance: "success" });
        closeRaiseModal();
        fetchOrderDetails(); // Refresh data
      } else {
        const errorMsg =
          response?.data?.result?.message || "Failed to raise issue";
        addToast(errorMsg, { appearance: "error" });
      }
    } catch (error) {
      addToast(error.message || "Failed to raise issue", {
        appearance: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    raiseIssueFormData,
    authData,
    storeId,
    dispatch,
    addToast,
    fetchOrderDetails,
  ]);

  const handleGoBack = () => navigate(-1);
  const handleGetInvoice = () => {
    if (orderData?.invoiceurl) window.open(orderData.invoiceurl, "_blank");
  };

  // Settlement action handlers
  const handleRequestLatestStatus = useCallback(
    async (settlementId) => {
      setSettlementLoading((prev) => ({ ...prev, [settlementId]: true }));
      try {
        const url = `${endpoint.RAISE_REPORT}/${settlementId}`;

        const response = await apiHandler({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          authToken: authData,
          dispatch,
          storeId,
        });
        //console.log("Request Latest Status Response:", response);
        const responseStatus = response?.data?.status || response?.status;
        const toastMessage =
          response?.data?.response?.data?.message ||
          "Latest status requested successfully";

        addToast(toastMessage, {
          appearance: responseStatus === 200 ? "success" : "error",
        });
      } catch (error) {
        //console.error("Error requesting latest status:", error);
        addToast(error.message || "Error: Failed to request latest status", {
          appearance: "error",
        });
      } finally {
        setSettlementLoading((prev) => ({ ...prev, [settlementId]: false }));
      }
    },
    [authData, dispatch, storeId, addToast],
  );

  const handleRaiseSettlementRecon = useCallback(
    async (settlementId) => {
      setReconLoading((prev) => ({ ...prev, [settlementId]: true }));
      try {
        const mongoOrderId =
          orderData.settlementDetails?.mongoOrderId || orderData._id;

        const payload = {
          order_details: [
            {
              order_id: mongoOrderId,
              settlement_id: settlementId,
            },
          ],
          app_details: {
            buyer_app_id: orderData.buyer_app_details?.bap_id,
            buyer_app_uri: orderData.buyer_app_details?.bap_uri,
            seller_app_id: orderData.sellerAppDetails?.url,
            seller_app_uri: orderData.sellerAppDetails?.id,
          },
        };

        //console.log("📤 Sending Settlement Recon Payload:", payload);

        const response = await apiHandler({
          url: endpoint.RAISE_RECON,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            tenantid: storeId,
          },
          authToken: authData,
          dispatch,
        });

        if (response?.status === 200 || response?.data?.result?.success) {
          addToast(
            "Reconciliation raised successfully. Check the Reconciliation section.",
            { appearance: "success" },
          );
          fetchOrderDetails(); // Refresh to show new recon
        } else {
          throw new Error(
            response?.data?.message || "Failed to raise reconciliation",
          );
        }
      } catch (error) {
        //console.error("Error raising reconciliation:", error);
        addToast(error.message || "Error: Failed to raise reconciliation", {
          appearance: "error",
        });
      } finally {
        setReconLoading((prev) => ({ ...prev, [settlementId]: false }));
      }
    },
    [orderData, authData, dispatch, storeId, addToast, fetchOrderDetails],
  );

  const handleResolutionSelect = (issueId, resolutionId) => {
    setSelectedResolutions((prev) => ({
      ...prev,
      [issueId]: {
        selected_resolution_id: resolutionId,
        accepted: true,
      },
    }));
  };

  const handleResolutionSubmit = useCallback(
    async (issueId) => {
      const selection = selectedResolutions[issueId];
      if (!selection) {
        addToast("Please select a resolution option before submitting.", {
          appearance: "warning",
        });
        return;
      }

      const payload = {
        // Following your required format: response: [{ selected_resolution_id, accepted }]
        response: [
          {
            selected_resolution_id: selection.selected_resolution_id,
            accepted: selection.accepted,
          },
        ],
      };

      try {
        setIsResolutionSubmitting(true);
        const response = await apiHandler({
          url: `${endpoint.RESPOND_TO_RESOLUTION}/${issueId}`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          authToken: authData,
          dispatch,
          storeId,
          data: payload,
        });

        if (response?.data?.result?.success || response?.status === 200) {
          addToast(response?.data?.result?.message, {
            appearance: "success",
          });
          fetchOrderDetails(); // refresh data
        } else {
          throw new Error(
            response?.data?.result?.message || "Failed to submit resolution",
          );
        }
      } catch (err) {
        addToast(err.message || "Error submitting resolution", {
          appearance: "error",
        });
      } finally {
        setIsResolutionSubmitting(false);
      }
    },
    [
      selectedResolutions,
      authData,
      dispatch,
      storeId,
      addToast,
      fetchOrderDetails,
    ],
  );

  return {
    orderData,
    loading,
    error,
    handleGoBack,
    handleGetInvoice,

    // Actions
    handleRaiseRecon,
    raisingRecon,

    handleLatestSettlement,
    sendingSettlement,

    handleRequestLatestStatus,
    handleRaiseSettlementRecon,
    settlementLoading,
    reconLoading,

    isRaiseModalOpen,
    openRaiseModal,
    closeRaiseModal,
    raiseIssueFormData,
    setRaiseIssueFormData,
    handleCategoryChange,
    categoryMapping,
    issueDomains,
    referenceType,
    subCategoryOptions,
    handleRaiseIssueSubmit,

    isSubmitting,
    handleRefChange,
    addRefRow,
    removeRefRow,
    selectedResolutions,
    handleResolutionSelect,
    handleResolutionSubmit,
    isResolutionSubmitting,
  };
};
